<script setup lang="ts">
	import { Float } from '@headlessui-float/vue'
	import {
		Dialog,
		DialogPanel,
		DialogTitle,
		Menu,
		MenuButton,
		MenuItem,
		MenuItems,
		TransitionChild,
		TransitionRoot,
	} from '@headlessui/vue'
	import { QuestionMarkCircleIcon } from '@heroicons/vue/20/solid'
	import { watchDebounced } from '@vueuse/core'
	import { computed, ref, watch } from 'vue'
	import { useToast } from 'vue-toastification'

	import DateRangeField from '@/components/DateRangeField.vue'

	import useAuthUser from '@/composables/useAuthUser'

	import useRecentHotelSearches from '../composables/useRecentHotelSearches'
	import type { RecentHotelSearch } from '../interfaces/recentHotelSearch'
	import RecentHotelSearchCover from './RecentHotelSearchCover.vue'

	interface Emits {
		/**
		 * Volver a llenar el formulario: el clic en una tarjeta con reserva (sin sus
		 * viajeros, ya ocupados en esas fechas) o la acción del menú.
		 */
		(event: 'fill', search: RecentHotelSearch): void
		/**
		 * Llenar el formulario y buscar en el mismo clic: el clic en una tarjeta sin
		 * reserva, con sus mismos viajeros, o «Buscar de nuevo» del menú.
		 */
		(event: 'search', search: RecentHotelSearch): void
	}

	const emits = defineEmits<Emits>()

	const { user } = useAuthUser()
	const toast = useToast()

	// El «?» del título: explica cómo funcionan las tarjetas y su menú.
	const helpOpen = ref(false)

	const {
		searches,
		visibility,
		filters,
		activeFilterCount,
		clearFilters,
		isLoading,
		isFetching,
		isFetchingNextPage,
		hasNextPage,
		fetchNextPage,
	} = useRecentHotelSearches()

	// El destino se escribe aquí y llega a los filtros 300 ms después de la última
	// tecla: cada cambio de filtro es una petición nueva.
	const destinationInput = ref(filters.value.destination)

	watchDebounced(
		destinationInput,
		(value) => (filters.value.destination = value),
		{ debounce: 300 }
	)

	// «Limpiar» también vacía la caja, que no se entera sola del cambio.
	watch(
		() => filters.value.destination,
		(value) => {
			if (value !== destinationInput.value) destinationInput.value = value
		}
	)

	// Con PROPIAS no hay nada que separar: todo lo que ve ya es suyo.
	const scopeOptions = computed(() => {
		if (!visibility.value || visibility.value === 'propias') return []

		return [
			{
				mine: false,
				label: visibility.value === 'empresa' ? 'Toda la empresa' : 'Mi equipo',
			},
			{ mine: true, label: 'Sólo las mías' },
		]
	})

	// La sección se queda aunque los filtros no encuentren nada, para poder quitarlos.
	const showSection = computed(
		() =>
			isLoading.value ||
			searches.value.length > 0 ||
			activeFilterCount.value > 0
	)

	// Tarjetas de esqueleto: seis al cargar (las dos filas que llegan) y tres más, una
	// fila, mientras llega «Ver más».
	const skeletonCards = computed(() => {
		if (isLoading.value) return 6

		return isFetchingNextPage.value ? 3 : 0
	})

	// Recarga en segundo plano (al volver a la pestaña): la lista se queda y sólo se avisa.
	const isRefreshing = computed(
		() => isFetching.value && !isLoading.value && !isFetchingNextPage.value
	)

	const dayMonth = new Intl.DateTimeFormat('es-MX', {
		day: '2-digit',
		month: '2-digit',
		year: 'numeric',
	})

	const relative = new Intl.RelativeTimeFormat('es-MX', { numeric: 'auto' })

	// «2026-10-16» con `new Date()` se lee como medianoche UTC y en México sale un día
	// antes; se arma con la fecha local.
	const formatDay = (date: string) => {
		const [year, month, day] = date.split('-').map(Number)

		return dayMonth.format(new Date(year, month - 1, day))
	}

	const timeAgo = (date: string | null) => {
		if (!date) return ''

		const minutes = Math.round((new Date(date).getTime() - Date.now()) / 60_000)

		if (Math.abs(minutes) < 60) return relative.format(minutes, 'minute')
		if (Math.abs(minutes) < 60 * 24)
			return relative.format(Math.round(minutes / 60), 'hour')
		if (Math.abs(minutes) < 60 * 24 * 7)
			return relative.format(Math.round(minutes / (60 * 24)), 'day')

		return dayMonth.format(new Date(date))
	}

	const plural = (count: number, singular: string, pluralForm: string) =>
		`${count} ${count === 1 ? singular : pluralForm}`

	const dayMonthYear = new Intl.DateTimeFormat('es-MX', {
		day: '2-digit',
		month: '2-digit',
		year: 'numeric',
	})

	const formatDayWithYear = (date: string) => {
		const [year, month, day] = date.split('-').map(Number)

		return dayMonthYear.format(new Date(year, month - 1, day))
	}

	/**
	 * La búsqueda en una línea, para pegarla en un correo o un chat. Con año: fuera de
	 * esta pantalla «6 nov» no dice de cuál.
	 */
	const copySearch = async (search: RecentHotelSearch) => {
		const text = [
			search.destinationLabel ?? search.city ?? 'Destino sin nombre',
			`${formatDayWithYear(search.checkIn)} – ${formatDayWithYear(search.checkOut)}`,
			plural(search.nights, 'noche', 'noches'),
			`${search.guestsPerRoom} por habitación`,
			travelerNames(search) ??
				plural(search.travelersCount, 'viajero', 'viajeros'),
		].join(' · ')

		try {
			await navigator.clipboard.writeText(text)
			toast.success('Búsqueda copiada')
		} catch {
			toast.error('No se pudo copiar la búsqueda')
		}
	}

	const actions = [
		{
			key: 'search',
			label: 'Buscar de nuevo',
			icon: 'search',
			run: (search: RecentHotelSearch) => emits('search', search),
		},
		{
			key: 'fill',
			label: 'Llenar el formulario',
			icon: 'edit_note',
			run: (search: RecentHotelSearch) => emits('fill', search),
		},
		{
			key: 'copy',
			label: 'Copiar datos',
			icon: 'content_copy',
			run: copySearch,
		},
	] as const

	/**
	 * Para quién fue la búsqueda: los dos primeros nombres y «y N más». Sólo vienen los
	 * viajeros que quien consulta puede elegir; los demás se cuentan en «más».
	 */
	const travelerNames = (search: RecentHotelSearch) => {
		const names = (search.travelers ?? []).map((traveler) => traveler.fullName)

		if (!names.length) return null

		const shown = names.slice(0, 2)
		const rest = Math.max(search.travelersCount, names.length) - shown.length

		return `${shown.join(', ')}${rest > 0 ? ` y ${rest} más` : ''}`
	}

	// Clic en la tarjeta: sin reserva busca de inmediato con sus mismos viajeros; con
	// reserva sólo llena el formulario, porque esos viajeros ya están ocupados.
	const openSearch = (search: RecentHotelSearch) => {
		if (search.booked) {
			emits('fill', search)
		} else {
			emits('search', search)
		}
	}

	// Quien ve búsquedas de otros (su gente o toda la empresa) necesita saber de quién es.
	const searchedByOther = (search: RecentHotelSearch) =>
		search.searchedBy && search.searchedBy.id !== user.value?.id ?
			search.searchedBy.name
		:	null
</script>

<template>
	<section
		v-if="showSection"
		class="mx-auto max-w-4xl px-4 sm:px-6 py-6"
		aria-labelledby="recent-hotel-searches-title"
	>
		<div class="mb-3 flex items-center gap-2">
			<span class="material-symbols-outlined text-[20px] text-[#253C70]"
				>history</span
			>
			<h2
				id="recent-hotel-searches-title"
				class="text-base font-semibold text-gray-900"
			>
				Últimas búsquedas
			</h2>
			<button
				type="button"
				class="cursor-pointer rounded-md bg-gray-100 p-0.5 transition-colors hover:bg-gray-200"
				aria-label="¿Cómo funcionan las últimas búsquedas?"
				@click="helpOpen = true"
			>
				<QuestionMarkCircleIcon
					class="h-4 w-4 text-gray-400"
					aria-hidden="true"
				/>
			</button>
			<span
				v-if="isRefreshing"
				class="ml-auto text-xs text-gray-400"
				aria-live="polite"
			>
				Actualizando…
			</span>
		</div>

		<!--
			Filtros: «Sólo las mías» sólo para quien ve búsquedas de otros; destino y
			fechas del viaje para todos.
		-->
		<div class="mb-4 flex flex-col gap-2 sm:flex-row sm:items-center">
			<div
				v-if="scopeOptions.length"
				class="inline-flex shrink-0 rounded-xl border border-gray-300 bg-white p-1"
				role="group"
				aria-label="De quién son las búsquedas"
			>
				<button
					v-for="option in scopeOptions"
					:key="option.label"
					type="button"
					:aria-pressed="filters.mine === option.mine"
					:class="[
						'rounded-lg px-3 py-1.5 text-sm font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#253C70]',
						filters.mine === option.mine ?
							'bg-[#253C70] text-white'
						:	'text-gray-600 hover:bg-gray-100',
					]"
					@click="filters.mine = option.mine"
				>
					{{ option.label }}
				</button>
			</div>

			<label class="relative block min-w-0 flex-1">
				<span class="sr-only">Buscar por destino u hotel</span>
				<span
					class="material-symbols-outlined pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[18px] text-gray-400"
					aria-hidden="true"
					>search</span
				>
				<input
					v-model="destinationInput"
					type="search"
					maxlength="100"
					placeholder="Destino u hotel"
					class="h-11 w-full rounded-xl border border-gray-300 bg-white pl-9 pr-3 text-sm text-gray-900 placeholder:text-gray-400 hover:border-gray-400 focus:border-[#021F58] focus:outline-none focus:ring-2 focus:ring-[#021F58]/20"
				/>
			</label>

			<div class="sm:w-64">
				<DateRangeField
					id="recent-hotel-searches-dates"
					v-model:from="filters.from"
					v-model:until="filters.to"
					placeholder="Fechas del viaje"
				/>
			</div>

			<button
				v-if="activeFilterCount"
				type="button"
				class="shrink-0 rounded-lg px-2 py-1.5 text-sm font-medium text-gray-600 hover:bg-gray-100 hover:text-gray-900"
				@click="clearFilters()"
			>
				Limpiar ({{ activeFilterCount }})
			</button>
		</div>

		<p
			v-if="!isLoading && !searches.length"
			class="rounded-xl border-2 border-dashed border-gray-200 px-4 py-8 text-center text-sm text-gray-500"
		>
			Ninguna búsqueda coincide con los filtros.
		</p>

		<!--
			Celular: tarjeta horizontal con miniatura (cinco tarjetas apiladas no deben ser
			kilométricas). Desde sm: cuadrícula con la portada arriba.
		-->
		<ul
			v-if="isLoading || searches.length"
			class="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3"
			:aria-busy="isLoading || isFetchingNextPage"
		>
			<li
				v-for="search in searches"
				:key="search.id"
				class="group relative overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm transition hover:border-gray-300 hover:shadow-md"
			>
				<button
					type="button"
					class="flex h-full w-full text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#253C70] sm:flex-col"
					:aria-label="
						search.booked ?
							`Llenar el formulario con la búsqueda de ${search.destinationLabel ?? 'este destino'}, ya reservada`
						:	`Buscar de nuevo ${search.destinationLabel ?? 'este destino'}`
					"
					@click="openSearch(search)"
				>
					<!-- Portada: la foto guardada en BookingV2 o un diseño propio. -->
					<RecentHotelSearchCover :search="search">
						<!-- Terminó en reserva: el ícono y, al pasar el cursor, el tooltip. -->
						<span
							v-if="search.booked"
							v-tooltip="'Esta búsqueda ya tiene una reserva a su cargo'"
							class="material-symbols-outlined absolute left-2 top-2 flex h-6 w-6 items-center justify-center rounded-full bg-white/95 text-[16px] text-emerald-600 shadow-sm"
							aria-hidden="true"
							>event_available</span
						>
					</RecentHotelSearchCover>

					<span class="flex min-w-0 flex-1 flex-col gap-1 p-3 pr-11 sm:pr-3">
						<span
							:class="[
								'line-clamp-2 text-sm font-semibold',
								search.booked ? 'text-gray-500' : 'text-gray-900',
							]"
						>
							{{
								search.destinationLabel ?? search.city ?? 'Destino sin nombre'
							}}
						</span>
						<span class="text-xs text-gray-500">
							{{ formatDay(search.checkIn) }} –
							{{ formatDay(search.checkOut) }} ·
							{{ plural(search.nights, 'noche', 'noches') }}
						</span>
						<span class="text-xs text-gray-500">
							{{ search.guestsPerRoom }} por habitación ·
							{{ plural(search.travelersCount, 'viajero', 'viajeros') }}
						</span>
						<span
							v-if="travelerNames(search)"
							class="flex min-w-0 items-center gap-1 text-xs text-gray-700"
						>
							<span
								class="material-symbols-outlined shrink-0 text-[16px] text-[#253C70]"
								aria-hidden="true"
								>person</span
							>
							<span class="truncate">{{ travelerNames(search) }}</span>
						</span>
						<span
							class="mt-auto flex items-center justify-between gap-2 pt-1 text-xs text-gray-400"
						>
							<span class="truncate">
								<template v-if="searchedByOther(search)"
									>Buscó {{ searchedByOther(search) }}</template
								>
							</span>
							<span class="shrink-0">{{ timeAgo(search.searchedAt) }}</span>
						</span>
					</span>
				</button>

				<!-- Con portal: la tarjeta recorta lo que sale de ella (overflow-hidden). -->
				<Menu
					as="div"
					class="absolute right-2 top-2"
				>
					<Float
						strategy="fixed"
						placement="bottom-end"
						:offset="4"
						:shift="true"
						:flip="true"
						:portal="true"
						:z-index="9999"
						enter="transition ease-out duration-100"
						enter-from="transform opacity-0 scale-95"
						enter-to="transform opacity-100 scale-100"
						leave="transition ease-in duration-75"
						leave-from="transform opacity-100 scale-100"
						leave-to="transform opacity-0 scale-95"
					>
						<MenuButton
							class="flex h-8 w-8 items-center justify-center rounded-full bg-white/90 text-gray-600 shadow-sm transition-colors hover:bg-white hover:text-gray-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#253C70]"
							:aria-label="`Más acciones para la búsqueda de ${search.destinationLabel ?? 'este destino'}`"
						>
							<span
								class="material-symbols-outlined text-[20px]"
								aria-hidden="true"
								>more_vert</span
							>
						</MenuButton>
						<MenuItems
							class="w-56 rounded-xl bg-white py-1 shadow-lg ring-1 ring-black/5 focus:outline-none"
						>
							<MenuItem
								v-for="action in actions"
								:key="action.key"
								v-slot="{ active }"
							>
								<button
									type="button"
									:class="[
										'flex w-full items-center gap-3 px-4 py-2 text-left text-sm',
										active ? 'bg-gray-100 text-gray-900' : 'text-gray-700',
									]"
									@click="action.run(search)"
								>
									<span
										class="material-symbols-outlined text-[18px] text-[#253C70]"
										aria-hidden="true"
										>{{ action.icon }}</span
									>
									{{ action.label }}
								</button>
							</MenuItem>
						</MenuItems>
					</Float>
				</Menu>
			</li>

			<li
				v-for="n in skeletonCards"
				:key="`skeleton-${n}`"
				class="flex overflow-hidden rounded-xl border border-gray-200 bg-white sm:flex-col"
				aria-hidden="true"
			>
				<span
					class="w-24 shrink-0 animate-pulse bg-gray-100 sm:h-28 sm:w-full"
				/>
				<span class="flex flex-1 flex-col gap-2 p-3">
					<span class="block h-3.5 w-3/4 animate-pulse rounded bg-gray-100" />
					<span class="block h-3 w-1/2 animate-pulse rounded bg-gray-100" />
					<span class="block h-3 w-2/3 animate-pulse rounded bg-gray-100" />
				</span>
			</li>
		</ul>

		<div
			v-if="hasNextPage && !isLoading"
			class="mt-4 flex justify-center"
		>
			<button
				type="button"
				class="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:opacity-60"
				:disabled="isFetchingNextPage"
				@click="fetchNextPage()"
			>
				{{ isFetchingNextPage ? 'Cargando…' : 'Ver más' }}
			</button>
		</div>

		<TransitionRoot
			appear
			:show="helpOpen"
			as="template"
		>
			<Dialog
				as="div"
				class="relative z-50"
				@close="helpOpen = false"
			>
				<TransitionChild
					as="template"
					enter="duration-300 ease-out"
					enter-from="opacity-0"
					enter-to="opacity-100"
					leave="duration-200 ease-in"
					leave-from="opacity-100"
					leave-to="opacity-0"
				>
					<div class="fixed inset-0 bg-black/25" />
				</TransitionChild>

				<div class="fixed inset-0 overflow-y-auto">
					<div class="flex min-h-full items-center justify-center p-4">
						<TransitionChild
							as="template"
							enter="duration-300 ease-out"
							enter-from="opacity-0 scale-95"
							enter-to="opacity-100 scale-100"
							leave="duration-200 ease-in"
							leave-from="opacity-100 scale-100"
							leave-to="opacity-0 scale-95"
						>
							<DialogPanel
								class="w-full max-w-md overflow-hidden rounded-xl bg-gray-50 p-6 text-left text-gray-900 shadow-xl"
							>
								<DialogTitle
									as="h3"
									class="flex items-center gap-2 text-xl font-semibold leading-6"
								>
									<span class="material-symbols-outlined text-[#253C70]"
										>history</span
									>
									Últimas búsquedas
								</DialogTitle>

								<ul class="mt-4 space-y-3 text-sm text-gray-700">
									<li>
										Aquí ves las búsquedas de hotel más recientes que puedes
										consultar: las tuyas y, según tu rol, las de las personas a
										tu cargo o las de tu empresa.
									</li>
									<li>
										Filtra por <strong>destino</strong> —sin preocuparte por los
										acentos— o por las <strong>fechas del viaje</strong>. Si ves
										búsquedas de otras personas,
										<strong>Sólo las mías</strong> deja nada más aquellas en las
										que tú vas de viajero.
									</li>
									<li>
										<strong>Clic en una tarjeta</strong>: repite la búsqueda de
										inmediato, con el mismo destino, fechas, huéspedes y
										viajeros.
									</li>
									<li class="flex gap-2">
										<span
											class="material-symbols-outlined mt-0.5 text-[18px] text-emerald-600"
											aria-hidden="true"
											>event_available</span
										>
										<span>
											Las tarjetas con este ícono ya tienen una reserva a su
											cargo. Al darles clic sólo se llena el formulario, sin los
											viajeros, para que elijas a quién reservar.
										</span>
									</li>
									<li class="flex gap-2">
										<span
											class="material-symbols-outlined mt-0.5 text-[18px] text-gray-500"
											aria-hidden="true"
											>more_vert</span
										>
										<span>
											En el menú de cada tarjeta puedes buscar de nuevo, llenar
											el formulario sin buscar o copiar los datos de la
											búsqueda.
										</span>
									</li>
									<li>
										Si a la búsqueda le falta algo —fechas que ya pasaron o
										viajeros que ya no puedes elegir—, se llena lo que se pueda
										y te decimos qué falta antes de buscar.
									</li>
									<li>«Ver más» muestra las siguientes seis búsquedas.</li>
								</ul>

								<div class="mt-6 flex justify-end">
									<button
										type="button"
										class="inline-flex justify-center rounded-md border border-transparent bg-yellow-200 px-4 py-2 text-sm font-medium text-yellow-900 hover:bg-yellow-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-yellow-400 focus-visible:ring-offset-2"
										@click="helpOpen = false"
									>
										Entendido
									</button>
								</div>
							</DialogPanel>
						</TransitionChild>
					</div>
				</div>
			</Dialog>
		</TransitionRoot>
	</section>
</template>
